'use client';

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Loader2, MoreVertical, Ban, Flag } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/hooks/useSessionHook';
import { useRealtimeDirectMessages } from '@/hooks/use-realtime-direct-messages';
import { useBlockUser } from '@/hooks/use-block-user';
import { useReportUser, ReportReason } from '@/hooks/use-report-user';
import { ChatWindow } from '@/components/chat/ChatWindow/ChatWindow';
import { MessageInput } from '@/components/messaging/MessageInput';
import supabase from '@/supabase';
import { uploadAttachment } from '@/lib/attachment-upload';
import type { DirectMessage } from '@/types/chat';

interface OtherParticipant {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export default function ConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;

  const [otherParticipant, setOtherParticipant] = useState<OtherParticipant | null>(null);
  const [isLoadingParticipant, setIsLoadingParticipant] = useState(true);
  const [participantError, setParticipantError] = useState<string | null>(null);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);

  // Block/Report dialog state
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>('harassment');
  const [reportDescription, setReportDescription] = useState('');
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const {
    blockUser,
    unblockUser,
    isBlocked,
    isLoading: isBlockLoading,
  } = useBlockUser(userId, otherParticipant?.id);

  const {
    reportUser,
    isLoading: isReportLoading,
  } = useReportUser(userId);

  const {
    messages,
    isLoading: isLoadingMessages,
    error: messagesError,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
  } = useRealtimeDirectMessages({
    conversationId: conversationId || '',
    userId: userId || '',
    enabled: !!conversationId && !!userId,
  });

  // Fetch the other participant's info
  useEffect(() => {
    const fetchOtherParticipant = async () => {
      if (!conversationId || !userId) {
        setIsLoadingParticipant(false);
        return;
      }

      try {
        // First get all participants for this conversation
        const { data: participants, error: participantsError } = await supabase
          .from('conversation_participants')
          .select('user_id, last_read_at')
          .eq('conversation_id', conversationId);

        if (participantsError) {
          throw participantsError;
        }

        // Find the other user (not the current user)
        const otherParticipantData = participants?.find(p => p.user_id !== userId);
        const currentUserParticipant = participants?.find(p => p.user_id === userId);

        if (!otherParticipantData) {
          setParticipantError('Conversation participant not found');
          setIsLoadingParticipant(false);
          return;
        }

        // Save last_read_at for read receipts
        if (currentUserParticipant?.last_read_at) {
          setLastReadAt(currentUserParticipant.last_read_at);
        }

        // Fetch the other participant's profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .eq('id', otherParticipantData.user_id)
          .single();

        if (profileError) {
          throw profileError;
        }

        setOtherParticipant(profile);
      } catch (err) {
        console.error('Error fetching other participant:', err);
        setParticipantError('Failed to load conversation');
      } finally {
        setIsLoadingParticipant(false);
      }
    };

    fetchOtherParticipant();
  }, [conversationId, userId]);

  // Update last_read_at when viewing messages
  useEffect(() => {
    const updateLastRead = async () => {
      if (!conversationId || !userId || messages.length === 0) return;

      // Update the last_read_at to mark messages as read
      await supabase
        .from('conversation_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('user_id', userId);
    };

    updateLastRead();
  }, [conversationId, userId, messages.length]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleBlockUser = async () => {
    if (!otherParticipant) return;

    const result = isBlocked
      ? await unblockUser(otherParticipant.id)
      : await blockUser(otherParticipant.id);

    if (result.success) {
      setActionSuccess(isBlocked ? 'User unblocked' : 'User blocked');
      setTimeout(() => setActionSuccess(null), 3000);
    }
    setShowBlockDialog(false);
  };

  const handleReportUser = async () => {
    if (!otherParticipant) return;

    const result = await reportUser({
      reportedUserId: otherParticipant.id,
      reason: reportReason,
      description: reportDescription || undefined,
      conversationId,
    });

    if (result.success) {
      setActionSuccess('Report submitted');
      setTimeout(() => setActionSuccess(null), 3000);
    }
    setShowReportDialog(false);
    setReportDescription('');
    setReportReason('harassment');
  };

  const handleSendMessage = useCallback(
    async (
      content: string,
      attachmentUrl?: string,
      attachmentType?: DirectMessage['attachment_type']
    ): Promise<{ success: boolean; error?: string }> => {
      return sendMessage(content, attachmentUrl, attachmentType);
    },
    [sendMessage]
  );

  const handleAttachmentUpload = useCallback(
    async (file: File): Promise<{ url: string; type: DirectMessage['attachment_type'] } | null> => {
      if (!userId) return null;

      const result = await uploadAttachment(file, userId);
      if (!result.success || !result.url) {
        return null;
      }

      const type: DirectMessage['attachment_type'] = file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('audio/')
        ? 'audio'
        : 'image';

      return { url: result.url, type };
    },
    [userId]
  );

  // Check if a message is read by the other participant
  const isMessageRead = useCallback(
    (messageCreatedAt: string): boolean => {
      if (!lastReadAt) return false;
      return new Date(messageCreatedAt) <= new Date(lastReadAt);
    },
    [lastReadAt]
  );

  const getInitials = (name: string | null): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Loading state
  if (isLoadingParticipant) {
    return (
      <div className="jr-app-shell">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[color:var(--jr-accent)]" />
        </div>
      </div>
    );
  }

  // Error state
  if (participantError || !conversationId) {
    return (
      <div className="jr-app-shell">
        <header className="flex items-center border-b border-[color:var(--jr-border)] bg-[color:rgba(34,20,19,0.86)] p-4 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="mr-2 rounded-full text-[color:var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
          >
            <ArrowLeft size={20} />
          </Button>
          <h2 className="jr-display text-2xl font-semibold text-[color:var(--jr-text)]">Conversation</h2>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="jr-status-error">{participantError || 'Conversation not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jr-app-shell">
      {/* Header with participant info */}
      <header className="flex shrink-0 items-center border-b border-[color:var(--jr-border)] bg-[linear-gradient(180deg,rgba(35,20,19,0.96),rgba(25,15,15,0.88))] p-4 backdrop-blur-xl">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="mr-2 rounded-full text-[color:var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
        >
          <ArrowLeft size={20} />
        </Button>
        {otherParticipant && (
          <div className="flex items-center flex-1">
            <Avatar className="jr-avatar-ring mr-3 h-10 w-10">
              <AvatarImage
                src={otherParticipant.avatar_url || undefined}
                alt={otherParticipant.display_name || 'User'}
              />
              <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-[color:var(--jr-accent)]">
                {getInitials(otherParticipant.display_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-[color:var(--jr-text)]">
                {otherParticipant.display_name || 'Unknown User'}
              </h2>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-[color:var(--jr-text-soft)] hover:bg-[color:rgba(255,255,255,0.06)] hover:text-[color:var(--jr-text)]"
                  data-testid="conversation-menu-button"
                >
                  <MoreVertical size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-[20px] border-[color:var(--jr-border)] bg-[color:rgba(35,23,22,0.98)] p-2 text-[color:var(--jr-text)] shadow-[0_18px_36px_rgba(0,0,0,0.32)]"
              >
                <DropdownMenuItem
                  data-testid="block-user-menu-item"
                  onClick={() => setShowBlockDialog(true)}
                  className="rounded-2xl text-[color:var(--jr-text)] focus:bg-[color:rgba(106,39,54,0.5)] focus:text-[color:var(--jr-text)]"
                >
                  <Ban className="mr-2 h-4 w-4" />
                  {isBlocked ? 'Unblock User' : 'Block User'}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[color:var(--jr-border)]" />
                <DropdownMenuItem
                  data-testid="report-user-menu-item"
                  onClick={() => setShowReportDialog(true)}
                  className="rounded-2xl text-[color:#efcf9b] focus:bg-[color:rgba(106,39,54,0.5)] focus:text-[color:#f4d59d]"
                >
                  <Flag className="mr-2 h-4 w-4" />
                  Report User
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </header>

      {/* Success notification */}
      {actionSuccess && (
        <div className="jr-status-success mx-4 mt-3">
          {actionSuccess}
        </div>
      )}

      {/* Block confirmation dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent className="jr-card rounded-[24px] border-[color:var(--jr-border)] p-6 text-[color:var(--jr-text)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="jr-display text-3xl text-[color:var(--jr-text)]">
              {isBlocked ? 'Unblock User' : 'Block User'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[color:var(--jr-text-soft)]">
              {isBlocked
                ? `Are you sure you want to unblock ${otherParticipant?.display_name || 'this user'}? They will be able to send you messages again.`
                : `Are you sure you want to block ${otherParticipant?.display_name || 'this user'}? They won't be able to send you messages.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isBlockLoading}
              className="jr-action-secondary border-[color:var(--jr-border)]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlockUser}
              disabled={isBlockLoading}
              className={isBlocked ? 'jr-action-secondary' : 'jr-action-danger'}
            >
              {isBlockLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isBlocked ? (
                'Unblock'
              ) : (
                'Block'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="jr-card rounded-[24px] border-[color:var(--jr-border)] p-6 text-[color:var(--jr-text)]">
          <DialogHeader>
            <DialogTitle className="jr-display text-3xl text-[color:var(--jr-text)]">Report User</DialogTitle>
            <DialogDescription className="text-[color:var(--jr-text-soft)]">
              Report {otherParticipant?.display_name || 'this user'} for inappropriate behavior.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[color:var(--jr-text)]">Reason</label>
              <select
                data-testid="report-reason-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value as ReportReason)}
                className="jr-input h-11 w-full appearance-none rounded-2xl px-3 py-2 text-sm"
              >
                <option value="harassment">Harassment</option>
                <option value="spam">Spam</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="scam">Scam</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[color:var(--jr-text)]">Additional Details (optional)</label>
              <Textarea
                data-testid="report-description-input"
                placeholder="Provide more details about the issue..."
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={3}
                className="jr-input min-h-[112px] rounded-[20px] px-4 py-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReportDialog(false)}
              disabled={isReportLoading}
              className="jr-action-secondary"
            >
              Cancel
            </Button>
            <Button
              data-testid="submit-report-button"
              onClick={handleReportUser}
              disabled={isReportLoading}
              className="jr-action-primary"
            >
              {isReportLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Submit Report'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages error */}
      {messagesError && (
        <div className="jr-status-error mx-4 mt-3">
          {messagesError}
        </div>
      )}

      {/* Chat window */}
      <ChatWindow
        messages={messages}
        currentUserId={userId || ''}
        isLoading={isLoadingMessages}
        hasMoreMessages={hasMoreMessages}
        onLoadMore={loadMoreMessages}
        isMessageRead={isMessageRead}
        className="min-h-0 flex-1"
      />

      {/* Message input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        onAttachmentUpload={handleAttachmentUpload}
        disabled={!userId}
        placeholder="Type a message..."
        className="shrink-0"
      />
    </div>
  );
}
